/**
 * Descriptor-driven Liquid Cooling Summary source (#997 — Story 4.2).
 *
 * Reproduces the bespoke `getPerLiquidCoolingCduSummaryDefinitions()` +
 * `getLiquidCoolingSharedSummaryDefinitions()` row sets and per-unit value /
 * title projection from the Liquid Cooling descriptor + the shared Summary
 * factory, byte-identically against the Story 4.2 golden masters
 * (`liquid-cooling-summary-rows.json`, `liquid-cooling-summary-filled.json`).
 * This is the engine-driven replacement Story 4.2 re-points the Summary and the
 * golden tests at — same oracle, new implementation.
 *
 * Row-set layout mirrors what the bespoke code produced from
 * `requestFieldRegistry.ts`:
 *
 *   - Per-unit editor group (`subsectionId: 'project'`): `name`, `status`,
 *     `lcCduType`, `lcDescription`, `lcVertivSuppliesRowManifolds`,
 *     `lcVertivSuppliesRackManifolds`, `lcCduRedundancy`,
 *     `lcLeakDetectionApproach` — the `subsection` marker stamps on the first
 *     row (via `buildEditorSubsectionSummaryRows`).
 *   - Per-unit note group (`subsectionId: 'additionalInformation'`):
 *     `lcAdditionalInformationSfn` — marker stamps here.
 *   - Shared group (`subsectionId: 'project'`): `lcLiquidToChipDesired` — a
 *     separate row set surfaced by a separate accessor because the Summary
 *     renders it ONCE regardless of unit type (bug 11 Summary facet).
 *
 * `labelOverrides` cover only the identity fields (`name`/`status`), whose
 * labels come from the editor field slice's `shortLabel` rather than any
 * `LIQUID_COOLING_FIELDS` registry entry (they are not registry fields).
 */

import { LIQUID_COOLING_DESCRIPTOR } from './liquidCooling.descriptor';
import { repeatableInstanceNameTypeRowValue } from '../../summary/repeatableInstanceSummaryDisplay';
import { buildEditorSubsectionSummaryRows } from '../fields/projections/editorSubsectionSummaryRows';
import { liquidCoolingSummaryRowValue } from '../fields/projections/summaryRowValues';
import {
  LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS,
  LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS,
  LIQUID_COOLING_NOTES_SUBSECTION_FIELDS,
} from '../fields/requestFieldRegistry';
import { LIQUID_COOLING_FIELDS } from '../fields/steps/liquid-cooling.fields';

import type { SummaryCardRow } from '../../summary/summaryCardSourceTypes';
import type { LiquidCoolingCduConfiguration } from '../../types/liquidCoolingCdu';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';

const RBAC_ANCHOR = LIQUID_COOLING_DESCRIPTOR.permissionRef.rbacAnchorDataField;
const SHARED_RBAC_ANCHOR = 'lcLiquidToChipDesired';

/**
 * Registry field metadata (labels) the factory resolves per field. Keeping this
 * pointed at the LIQUID_COOLING_FIELDS registry (rather than the descriptor's
 * `fields`) mirrors the bespoke source and keeps labels/subsection metadata
 * flowing through the same lookup the engine expects.
 */
const REGISTRY_FIELDS = LIQUID_COOLING_FIELDS;

/** Editor field slice minus the shared key (matches bespoke `editorFields`). */
const LC_SHARED_FIELD_SET = new Set<string>(
  LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS,
);
const PER_UNIT_EDITOR_FIELDS: readonly SubsectionFieldDescriptor[] =
  LIQUID_COOLING_CDU_EDITOR_SUBSECTION_FIELDS.filter(
    field => !LC_SHARED_FIELD_SET.has(field.key),
  );

/** Shared editor field slice (shortLabels lifted from LIQUID_COOLING_FIELDS). */
const SHARED_EDITOR_FIELDS: readonly SubsectionFieldDescriptor[] =
  LIQUID_COOLING_CDU_EDITOR_SHARED_FIELD_KEYS.map(key => ({
    key,
    shortLabel:
      LIQUID_COOLING_FIELDS.find(field => field.dataField === key)
        ?.shortLabel ?? key,
  }));

function buildGroup(
  editorFields: readonly SubsectionFieldDescriptor[],
  subsectionId: string,
  rbacAnchorDataField: string,
): SummaryCardRow[] {
  return buildEditorSubsectionSummaryRows({
    editorFields,
    registryFields: REGISTRY_FIELDS,
    rbacAnchorDataField,
    subsectionId,
  });
}

/**
 * Full ordered per-unit Liquid Cooling Summary row set (editor + note),
 * byte-identical to the bespoke
 * `getPerLiquidCoolingCduSummaryDefinitions()` output.
 */
export function getLiquidCoolingDescriptorSummaryDefinitions(): SummaryCardRow[] {
  return [
    ...buildGroup(PER_UNIT_EDITOR_FIELDS, 'project', RBAC_ANCHOR),
    ...buildGroup(
      LIQUID_COOLING_NOTES_SUBSECTION_FIELDS,
      'additionalInformation',
      'lcAdditionalInformationSfn',
    ),
  ];
}

/**
 * Shared Liquid Cooling Summary row set (`lcLiquidToChipDesired`), byte-identical
 * to the bespoke `getLiquidCoolingSharedSummaryDefinitions()` output. Rendered
 * once by the Summary regardless of unit type (bug 11 Summary facet).
 */
export function getLiquidCoolingDescriptorSharedSummaryDefinitions(): SummaryCardRow[] {
  return buildGroup(SHARED_EDITOR_FIELDS, 'project', SHARED_RBAC_ANCHOR);
}

/**
 * Per-unit Liquid Cooling CDU summary value — delegates to
 * `repeatableInstanceNameTypeRowValue` for the identity fields (`name`, Existing/
 * New `status`) and `liquidCoolingSummaryRowValue` for the CDU detail fields
 * (Yes/No formatting for the manifold booleans + the shared liquid-to-chip
 * boolean). Re-homed here so the Summary value path no longer routes through
 * the bespoke display module.
 */
export function liquidCoolingDescriptorUnitRowValue(
  unit: LiquidCoolingCduConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  const nameTypeValue = repeatableInstanceNameTypeRowValue(unit, dataField);
  if (nameTypeValue !== undefined) {
    return nameTypeValue;
  }
  return liquidCoolingSummaryRowValue(
    unit as unknown as Record<string, unknown>,
    dataField,
  );
}

/**
 * Resolve a CDU's display name (user-set `name` → `CDU N` fallback based on
 * `displayOrder`, else `null` so the caller falls back to the ordinal). Kept
 * here so the Summary title no longer depends on the bespoke display module.
 */
function resolveLiquidCoolingCduSummaryDisplayName(
  cdu: LiquidCoolingCduConfiguration,
): string | null {
  const name = cdu.name?.trim();
  if (name) {
    return name;
  }
  if (cdu.displayOrder != null && cdu.displayOrder > 0) {
    return `CDU ${cdu.displayOrder}`;
  }
  return null;
}

/** Per-unit Summary card title — `Existing/New — <name>` (fallback `… CDU N`). */
export function getLiquidCoolingDescriptorUnitTitle(
  cdu: LiquidCoolingCduConfiguration,
  index: number,
): string {
  const status = cdu.status === 'existing' ? 'Existing' : 'New';
  const name = resolveLiquidCoolingCduSummaryDisplayName(cdu);
  return name ? `${status} — ${name}` : `${status} CDU ${index + 1}`;
}
